import React from 'react';
import { Modal, ModalBody, ModalHeader, Button, FormGroup, Label, Input, Form, Row, Col } from 'reactstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { SketchPicker } from 'react-color';

const NewServiceModal = ({ isOpen, toggle, onSave, service, onInputChange, resourceTypes = [], resources = [], organization = null }) => {
    const availableResourceTypes = React.useMemo(() => {
        if (!service.resource_type) {
            return resourceTypes;
        }
        
        const typeExists = resourceTypes.some(type => type.id === service.resource_type);
        if (typeExists) {
            return resourceTypes;
        }

        return [...resourceTypes, { id: service.resource_type, name: service.resource_type }];
    }, [resourceTypes, service.resource_type]);

    const maxResourceCount = React.useMemo(() => {
        if (!service.resource_type) {
            return 0;
        }
        return resources.filter(r => r.resource_type === service.resource_type).length;
    }, [resources, service.resource_type]);
    
    const handleColorChange = (color) => {
        onInputChange('color_code', color.hex);
    };

    return (
        <Modal isOpen={isOpen} toggle={toggle} autoFocus={false} size='lg'>
            <ModalHeader toggle={toggle}>Add Service</ModalHeader>
            <ModalBody>
                <Form onSubmit={(e) => { e.preventDefault(); onSave(service); }}>
                    <Row>
                        <Col xs={12} md={8}>
                            <FormGroup>
                                <Label for="title">Title</Label>
                                <Input
                                    type="text"
                                    id="title"
                                    name="title"
                                    value={service.title}
                                    onChange={(e) => onInputChange('title', e.target.value)}
                                    placeholder="Set Title"
                                    autoFocus={true}
                                    required
                                />
                            </FormGroup>

                            <FormGroup>
                                <Label for="description">Description</Label>
                                <textarea
                                    id="description"
                                    name="description"
                                    value={service.description}
                                    onChange={(e) => onInputChange('description', e.target.value)}
                                    className="form-control"></textarea>
                            </FormGroup>

                            <FormGroup>
                                <Label for="expectations">Expectations</Label>
                                <textarea
                                    id="expectations"
                                    name="expectations"
                                    value={service.expectations}
                                    onChange={(e) => onInputChange('expectations', e.target.value)}
                                    className="form-control"></textarea>
                            </FormGroup>

                            <FormGroup>
                                <Label for="duration">Duration (min)</Label>
                                <Input
                                    type="number"
                                    id="duration"
                                    name="duration"
                                    value={service.duration}
                                    onChange={(e) => onInputChange('duration', e.target.value)}
                                />
                            </FormGroup>

                            <FormGroup>
                                <Label for="resource_type">Resource Type</Label>
                                <Input
                                    type="select"
                                    id="resource_type"
                                    name="resource_type"
                                    value={service.resource_type || ''}
                                    onChange={(e) => onInputChange('resource_type', e.target.value)}
                                    required
                                >
                                    <option value="">Select resource type</option>
                                    {availableResourceTypes.map((type, index) => (
                                        <option key={`${type.id}-${index}`} value={type.id}>{type.name}</option>
                                    ))}
                                </Input>
                            </FormGroup>

                            <FormGroup>
                                <Label for="resource_count">Resource Count</Label>
                                <Input
                                    type="number"
                                    id="resource_count"
                                    name="resource_count"
                                    value={service.resource_count || 1}
                                    onChange={(e) => onInputChange('resource_count', e.target.value)}
                                    min="1"
                                    max={maxResourceCount || 999}
                                    disabled={!service.resource_type}
                                />
                                {service.resource_type && maxResourceCount > 0 && (
                                    <small className="form-text text-muted">
                                        Max: {maxResourceCount} resource{maxResourceCount !== 1 ? 's' : ''} available
                                    </small>
                                )}
                            </FormGroup>

                            <FormGroup>
                                <Label for="payment_option">Payment Option</Label>
                                <Input
                                    type="select"
                                    id="payment_option"
                                    name="payment_option"
                                    value={service.payment_option || 'None'}
                                    onChange={(e) => onInputChange('payment_option', e.target.value)}
                                >
                                    {organization?.accept_payments ? (
                                        <>
                                            <option value="None">None - No payment required</option>
                                            <option value="PrePaid">PrePaid - Charge immediately upon booking</option>
                                            <option value="Hold">Hold - Authorize card, charge on no-show</option>
                                        </>
                                    ) : (
                                        <option value="None">None - No payment required</option>
                                    )}
                                </Input>
                            </FormGroup>

                            <FormGroup>
                                <Label for="online_payment_text">Online Payment Text</Label>
                                <textarea
                                    id="online_payment_text"
                                    name="online_payment_text"
                                    value={service.online_payment_text || ''}
                                    onChange={(e) => onInputChange('online_payment_text', e.target.value)}
                                    className="form-control"
                                    placeholder="Custom text displayed to customers during online payment"
                                ></textarea>
                            </FormGroup>
                        </Col>
                        <Col xs={12} md={4}>

                            <FormGroup>
                                <Label for="color_code">Color Code</Label>
                                <Input
                                    type="text"
                                    id="color_code"
                                    name="color_code"
                                    value={service.color_code}
                                    onChange={(e) => onInputChange('color_code', e.target.value)}
                                />
                                <div style={{ marginTop: '10px' }}>
                                    <SketchPicker
                                        color={service.color_code}
                                        onChange={handleColorChange}
                                    />
                                </div>
                            </FormGroup>

                            <FormGroup>
                                <Label for="valid_from">Valid From</Label>
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <DatePicker
                                        id="valid_from"
                                        name="valid_from"
                                        className="form-control"
                                        selected={service.valid_from}
                                        onChange={(date) => onInputChange('valid_from', date)}
                                        style={{ marginLeft: '10px' }}
                                        value={service.valid_from}
                                    />
                                </div>
                            </FormGroup>

                            <FormGroup>
                                <Label for="valid_to">Valid To</Label>
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <DatePicker
                                        id="valid_to"
                                        name="valid_to"
                                        className="form-control"
                                        selected={service.valid_to}
                                        onChange={(date) => onInputChange('valid_to', date)}
                                        style={{ marginLeft: '10px' }}
                                        value={service.valid_to}
                                    />
                                </div>
                            </FormGroup>

                            <FormGroup style={{ marginTop: '20px' }}>
                                <Label for="sort">Sort</Label>
                                <Input
                                    type="number"
                                    id="sort"
                                    name="sort"
                                    value={service.sort}
                                    onChange={(e) => onInputChange('sort', e.target.value)}
                                />
                            </FormGroup>

                            <FormGroup check style={{ marginTop: '15px' }}>
                                <Label check>
                                    <Input
                                        type="checkbox"
                                        id="internal"
                                        name="internal"
                                        checked={service.internal}
                                        onChange={(e) => onInputChange('internal', e.target.checked)}
                                    />
                                    {' '}Internal
                                </Label>
                            </FormGroup>

                        </Col>
                    </Row>
                    <Button color="primary" type="submit">Save</Button>{' '}
                    <Button color="secondary" onClick={toggle}>Cancel</Button>
                </Form>
            </ModalBody>
        </Modal>
    );
};

export default NewServiceModal;
